import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { confirmImportPublishers, importPublishers } from '../../../../api/publisherApi';
import TableContainer from '../../../../components/TableContainerReactTable';
import { cnpjMask } from '../../../../utils';
import { Publisher } from '../../../../types/publisher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle2, ArrowLeft, ArrowRight, X } from 'lucide-react';

interface ImportPublisherModalProps {
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Publisher[]) => void;
}

const ImportPublisherModal = ({ isOpen, toggle, onAddData }: ImportPublisherModalProps) => {
    const [activeTab, setActiveTab] = useState<'import' | 'confirm' | 'success'>('import');
    const [firstStepLoading, setFirstStepLoading] = useState<boolean>(false);
    const [secondStepLoading, setSecondStepLoading] = useState<boolean>(false);
    const [publishersToSave, setPublishersToSave] = useState<(Publisher & { errors: string[] })[]>([]);

    const toggleImportPublisherModal = () => {
        toggle();
        setActiveTab('import');
        handleFirstStepForm.resetForm();
        setPublishersToSave([]);
    };

    const publishersToAddColumns = useMemo(
        () => [
            {
                header: 'Mídia Cad',
                accessorKey: 'mediaCad',
                enableColumnFilter: false,
            },
            {
                header: 'Nome',
                accessorKey: 'name',
                enableColumnFilter: false,
            },
            {
                header: 'CNPJ',
                accessorFn: (row: any) => {
                    return row.cnpj ? cnpjMask(row.cnpj) : '';
                },
                enableColumnFilter: false,
            },
            {
                header: 'Site',
                accessorKey: 'site',
                enableColumnFilter: false,
            },
            {
                header: 'App',
                accessorKey: 'app',
                enableColumnFilter: false,
            },
            {
                header: 'Mídia Kit',
                accessorFn: (row: any) => {
                    return row.mediaKitUrl || row.mediaKitFile;
                },
                cell: (cell: any) => {
                    const cellValue = cell.getValue();
                    if (cellValue) {
                        return (<>{cellValue} <a
                            href={cellValue}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <i className="ri-external-link-line"></i>
                        </a></>);
                    }

                    return '-';

                },
                enableColumnFilter: false,
            },
            {
                header: 'Recursos de acessibilidade',
                accessorKey: 'accessibilityResources',
                enableColumnFilter: false,
            },
            {
                header: 'Erros',
                accessorFn: (row: any) => {
                    return row.errors.length === 0 ? '0' : row.errors.join(';\n');
                },
                enableColumnFilter: false,
            },
        ],
        [],
    );

    const handleFirstStepForm: any = useFormik({
        enableReinitialize: true,

        initialValues: {
            file: undefined,
        },

        validationSchema: Yup.object({
            file: Yup.mixed().required('É necessário um arquivo csv'),
        }),
        onSubmit: () => {
            setFirstStepLoading(true);
            const formData = new FormData();
            const file: HTMLInputElement | null = document.querySelector('#file-field');
            if (file && file.files) {
                formData.append('file', file.files[0]);
            }
            return importPublishers(formData)
                .then((res: any) => {
                    setPublishersToSave(res);
                    setActiveTab('confirm');
                })
                .catch(() => {})
                .finally(() => {
                    setFirstStepLoading(false);
                });
        },
    });

    // Second Step Form Validation
    const handleSecondStepForm = (event: any) => {
        event.preventDefault();
        const publishersToSaveCount = publishersToSave.length;
        if (!window.confirm(`Deseja realmente importar ${publishersToSaveCount} publishers?`)) {
            return;
        }

        const countWarnings = publishersToSave.filter((publisher) => publisher.errors.length > 0).length;
        if (countWarnings) {
            if (!window.confirm(`Existem ${countWarnings} publishers com alertas. Deseja seguir mesmo assim?`)) {
                return;
            }
        }
        const countErrors = publishersToSave.filter((publisher) => publisher.errors.length > 0).length;
        if (countErrors) {
            window.alert(`Existem ${countErrors} publishers com erros. Não é possível importar publishers com erros.`);
            return;
        }

        setSecondStepLoading(true);

        confirmImportPublishers(publishersToSave)
        .then((res: any) => {
            toggleImportPublisherModal();
            toast.success('Publishers importados com sucesso!');
            onAddData(res);
        })
        .catch(() => {})
        .finally(() => {
            setSecondStepLoading(false);
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={toggleImportPublisherModal}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Importar publishers</DialogTitle>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="import" onClick={() => activeTab === 'confirm' && setActiveTab('import')}>
                            Importe
                        </TabsTrigger>
                        <TabsTrigger value="confirm" disabled={publishersToSave.length === 0}>
                            Confirme
                        </TabsTrigger>
                        <TabsTrigger value="success" disabled={activeTab !== 'success'}>
                            Concluído
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="import" className="space-y-4 mt-4">
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFirstStepForm.handleSubmit();
                            return false;
                        }} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="file-field">
                                    Publishers
                                </Label>
                                <Input
                                    type="file"
                                    id="file-field"
                                    name="file"
                                    accept=".csv"
                                    onChange={handleFirstStepForm.handleChange}
                                    onBlur={handleFirstStepForm.handleBlur}
                                    value={handleFirstStepForm.values.file}
                                />
                                {handleFirstStepForm.touched.file && handleFirstStepForm.errors.file && (
                                    <p className="text-sm text-red-500">{handleFirstStepForm.errors.file}</p>
                                )}
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    type="submit"
                                    disabled={firstStepLoading}
                                    className="flex items-center gap-2"
                                >
                                    {firstStepLoading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Carregando...
                                        </>
                                    ) : (
                                        <>
                                            Importar
                                            <ArrowRight className="h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </TabsContent>

                    <TabsContent value="confirm" className="space-y-4 mt-4">
                        <TableContainer
                            divClass="table-responsive"
                            columns={(publishersToAddColumns || [])}
                            data={(publishersToSave || [])}
                            isGlobalFilter={true}
                            customPageSize={30}
                            SearchPlaceholder="Procurar..."
                        />

                        <div className="flex justify-between">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setActiveTab('import')}
                                className="flex items-center gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Cancelar & Voltar
                            </Button>
                            <Button
                                type="button"
                                disabled={secondStepLoading}
                                onClick={handleSecondStepForm}
                                className="flex items-center gap-2"
                            >
                                {secondStepLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Carregando...
                                    </>
                                ) : (
                                    <>
                                        Confirmar
                                        <ArrowRight className="h-4 w-4" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </TabsContent>

                    <TabsContent value="success" className="mt-4">
                        <div className="text-center py-8">
                            <div className="flex justify-center mb-4">
                                <div className="rounded-full bg-green-100 p-6">
                                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                                </div>
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Muito bom!</h3>
                            <p className="text-muted-foreground">
                                Seus publishers foram importados com sucesso.
                            </p>
                            <div className="mt-6">
                                <Button onClick={toggleImportPublisherModal} className="flex items-center gap-2">
                                    <X className="h-4 w-4" />
                                    Fechar
                                </Button>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

export default ImportPublisherModal;
